import { useState, useCallback, useEffect } from "react";
import { Node, Edge, useNodesState, useEdgesState } from "reactflow";
import { getAccounts, getAccountsByFundId } from "../../api/Accounts";
import { Account as APIAccount } from "../../types/Account";
import { useLocation } from "react-router-dom";
import { useAtom } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";

export interface Account {
  id: string;
  code: string;
  name: string;
  children?: Account[];
  isExpanded?: boolean;
}

export function useChartOfAccountsController() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [viewContext, setViewContext] = useState<"client" | "fund">("client");
  const [selectedFund] = useAtom(SelectedFundAtom);

  const location = useLocation();

  useEffect(() => {
    // Parse context from URL query parameters
    const params = new URLSearchParams(location.search);
    const context = params.get("context");
    if (context === "fund") {
      setViewContext("fund");
    } else {
      setViewContext("client");
    }
  }, [location.search]);

  // Transform API accounts to chart of accounts structure
  const transformAccounts = useCallback(
    (apiAccounts: APIAccount[]): Account[] => {
      // Create a map to store accounts by their ID for easy lookup
      const accountMap = new Map<number, Account>();

      // First pass: Create account objects
      apiAccounts.forEach((apiAccount) => {
        accountMap.set(apiAccount.accountId, {
          id: apiAccount.accountId.toString(),
          code: apiAccount.accountKey,
          name: apiAccount.name,
          isExpanded: false,
          children: [],
        });
      });

      // Second pass: Build the hierarchy
      const rootAccounts: Account[] = [];

      apiAccounts.forEach((apiAccount) => {
        const account = accountMap.get(apiAccount.accountId);

        if (account) {
          if (
            apiAccount.parentAccountId &&
            accountMap.has(apiAccount.parentAccountId)
          ) {
            // This is a child account
            const parent = accountMap.get(apiAccount.parentAccountId);
            if (parent && parent.children) {
              parent.children.push(account);
            }
          } else {
            // This is a root account
            rootAccounts.push(account);
          }
        }
      });

      // Set top-level accounts to be expanded by default
      rootAccounts.forEach((account) => {
        account.isExpanded = true;
      });

      return rootAccounts;
    },
    []
  );

  // Fetch accounts from the API
  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let apiAccounts: APIAccount[] = [];

      // If in fund context and a fund is selected, get accounts specific to that fund
      if (viewContext === "fund" && selectedFund) {
        apiAccounts = await getAccountsByFundId(selectedFund.fundId);
      } else {
        // Otherwise get all accounts
        apiAccounts = await getAccounts();
      }

      const transformedAccounts = transformAccounts(apiAccounts);
      setAccounts(transformedAccounts);
    } catch (err) {
      console.error("Error fetching accounts:", err);
      setError("Failed to fetch accounts.");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [transformAccounts, viewContext, selectedFund]);

  // Call fetchAccounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const [searchTerm, setSearchTerm] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Convert hierarchical account data to flat nodes and edges for React Flow
  const createGraphData = useCallback(() => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    // Position variables for layout
    const levelHeight = 200; // Increased vertical spacing between levels
    const topLevelSpacing = 200; // Increased spacing between top level nodes
    const childSpacing = 280; // Increased spacing between child nodes

    // First pass: Count visible nodes at each level to calculate horizontal positions
    const levelCounts: Record<number, number> = {};
    const levelPositions: Record<number, number[]> = {};

    // Process accounts recursively
    const countVisibleNodes = (
      account: Account,
      level: number = 0
    ): boolean => {
      const isParent = account.children && account.children.length > 0;

      // Check if account matches search term
      if (
        searchTerm &&
        !account.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !account.code.includes(searchTerm) &&
        !isParent
      ) {
        return false;
      }

      // Initialize level count if not already done
      if (!levelCounts[level]) {
        levelCounts[level] = 0;
        levelPositions[level] = [];
      }

      // Count this node
      levelCounts[level]++;

      // Count children recursively
      let hasVisibleChildren = false;
      if (isParent && account.isExpanded) {
        for (const child of account.children!) {
          const childVisible = countVisibleNodes(child, level + 1);
          hasVisibleChildren = hasVisibleChildren || childVisible;
        }
      }

      return true;
    };

    // First pass to count nodes
    accounts.forEach((account) => {
      countVisibleNodes(account);
    });

    // Second pass: Generate nodes and edges with correct positioning
    const processAccount = (
      account: Account,
      level: number = 0,
      parentIndex: number = -1
    ): { nodeIndices: number[]; width: number } => {
      const isParent = account.children && account.children.length > 0;
      const nodeIndices: number[] = [];

      // Check if account matches search term
      if (
        searchTerm &&
        !account.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !account.code.includes(searchTerm) &&
        !isParent
      ) {
        return { nodeIndices: [], width: 0 };
      }

      // Current node position
      if (!levelPositions[level]) {
        levelPositions[level] = [];
      }

      // Calculate x position
      let xPos: number;
      if (level === 0) {
        // Top level nodes are spaced evenly
        xPos = levelPositions[level].length * topLevelSpacing;
      } else {
        // For children, try to center them under parent when possible
        const parentPos =
          parentIndex >= 0 && newNodes[parentIndex]
            ? newNodes[parentIndex].position.x
            : 0;
        xPos =
          parentPos +
          (levelPositions[level].length -
            (levelPositions[level].length > 0
              ? levelPositions[level].length / 2
              : 0)) *
            childSpacing;

        // Add an offset to stagger children for better tree visualization
        const staggerOffset = level % 2 === 0 ? 30 : -30;
        xPos += staggerOffset;
      }

      levelPositions[level].push(xPos);

      // Create the node
      const nodeIndex = newNodes.length;
      nodeIndices.push(nodeIndex);

      const node: Node = {
        id: account.id,
        type: "accountNode",
        position: { x: xPos, y: level * levelHeight },
        data: {
          ...account,
          isParent,
          width: 200,
        },
      };

      newNodes.push(node);

      // Process children recursively
      let totalWidth = childSpacing; // Minimum width
      if (isParent && account.isExpanded) {
        const childResults: { nodeIndices: number[]; width: number }[] = [];
        let childrenWidth = 0;

        // First, collect all visible children
        account.children!.forEach((child) => {
          const result = processAccount(child, level + 1, nodeIndex);
          if (result.nodeIndices.length > 0) {
            childResults.push(result);
            childrenWidth += result.width;
          }
        });

        // After all children are processed, create edges
        childResults.forEach((result) => {
          result.nodeIndices.forEach((childIndex) => {
            if (childIndex < newNodes.length) {
              newEdges.push({
                id: `${account.id}-${newNodes[childIndex].id}`,
                source: account.id,
                target: newNodes[childIndex].id,
                style: { stroke: "#888" },
                animated: true,
              });
            }
          });
        });

        // Adjust parent node position to center over children if needed
        if (childResults.length > 0) {
          // Find leftmost and rightmost child
          const firstChild = newNodes[childResults[0].nodeIndices[0]];
          const lastChild =
            newNodes[childResults[childResults.length - 1].nodeIndices[0]];

          if (firstChild && lastChild) {
            const leftmostPos = firstChild.position.x;
            const rightmostPos = lastChild.position.x;
            const centerPos = leftmostPos + (rightmostPos - leftmostPos) / 2;

            // Center current node over its children
            if (Math.abs(node.position.x - centerPos) > 50) {
              node.position.x = centerPos;
            }

            totalWidth = Math.max(
              childrenWidth,
              rightmostPos - leftmostPos + childSpacing
            );
          }
        }
      }

      return { nodeIndices, width: totalWidth };
    };

    // Initialize positions for each level
    Object.keys(levelCounts).forEach((level) => {
      levelPositions[parseInt(level)] = [];
    });

    // Process each top-level account
    accounts.forEach((account) => {
      processAccount(account);
    });

    // Final adjustment to avoid overlaps
    const usedPositions: Record<number, Set<number>> = {};

    newNodes.forEach((node) => {
      const level = Math.round(node.position.y / levelHeight);
      if (!usedPositions[level]) {
        usedPositions[level] = new Set();
      }

      // Check if this position is already used (with some margin)
      const margin = 210;
      let xPos = node.position.x;
      let shifted = false;

      for (const pos of Array.from(usedPositions[level])) {
        if (Math.abs(pos - xPos) < margin) {
          // Position already taken, shift to the right
          xPos = pos + margin;
          shifted = true;
        }
      }

      if (shifted) {
        node.position.x = xPos;
      }

      usedPositions[level].add(xPos);
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [accounts, searchTerm, setNodes, setEdges]);

  // Toggle the expanded state of an account
  const toggleExpand = (accountId: string) => {
    // Start the animation
    setIsAnimating(true);

    const updateAccountsRecursively = (accts: Account[]): Account[] => {
      return accts.map((account) => {
        if (account.id === accountId) {
          return { ...account, isExpanded: !account.isExpanded };
        }
        if (account.children) {
          return {
            ...account,
            children: updateAccountsRecursively(account.children),
          };
        }
        return account;
      });
    };

    setAccounts(updateAccountsRecursively(accounts));

    // After toggling, allow a short delay for the animation before updating the graph
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  // Double-click handler for nodes to toggle expand/collapse
  const onNodeDoubleClick = (_event: React.MouseEvent, node: Node) => {
    if (node.data.isParent && !isAnimating) {
      toggleExpand(node.id);
    }
  };

  // Update graph data when accounts or search changes
  useEffect(() => {
    // Only update the graph if we're not in an animation
    if (!isAnimating) {
      const timer = setTimeout(() => {
        createGraphData();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [accounts, searchTerm, createGraphData, isAnimating]);

  // Fit view after graph data changes
  useEffect(() => {
    if (nodes.length > 0) {
      // Add a small delay to let React Flow update the DOM
      const timer = setTimeout(() => {
        const reactFlowInstance = document.querySelector(".react-flow");
        if (reactFlowInstance) {
          // Trigger a window resize event to make React Flow recalculate layout
          window.dispatchEvent(new Event("resize"));
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [nodes]);

  // Return everything needed by the view component
  return {
    accounts,
    setAccounts,
    searchTerm,
    setSearchTerm,
    loading,
    error,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    onNodeDoubleClick,
    toggleExpand,
    refreshAccounts: fetchAccounts,
    isLoading: loading,
    viewContext,
  };
}
